import { Link } from "react-router-dom";
import { Button } from "../../Components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function AIPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <img
              src="/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png"
              alt="Measure Ignite"
              className="h-12 sm:h-14 w-auto"
            />
          </div>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl">
        {/* Hero */}
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Measure Ignite
          </h1>
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#FF4D00] mb-6">
            Responsible AI Use Policy
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            Measure Ignite uses artificial intelligence (AI) as a supportive, assistive technology to enhance clarity, organization, and reporting, not as a decision-making authority. All AI use is intentionally bounded to protect community agency, human judgment, and ethical accountability.
          </p>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          {/* Introduction */}
          <div className="bg-[#FFF5F0] p-6 rounded-xl">
            <p className="text-gray-700 leading-relaxed m-0">
              This policy aligns with global best practices for trustworthy, human-centered AI as articulated by leading AI governance frameworks.
            </p>
          </div>

          {/* Purpose & Scope */}
          <section>
            <div className="border-l-4 border-[#FF4D00] pl-6 py-2">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Purpose & Scope
              </h3>
              <p className="text-gray-700 mb-3">AI within Ignite is used solely to:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Organize and structure user-entered information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Assist with drafting and formatting reports</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Assist users in creating and generating language and communications information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Reduce repetitive administrative work</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Recommend new networking connections</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Support reflection on patterns within user-provided data</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                AI systems in Ignite operate under explicit human oversight and do not act autonomously.
              </p>
            </div>
          </section>

          {/* Explicit Limitations */}
          <section>
            <div className="border-l-4 border-[#E8973E] pl-6 py-2">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Explicit Limitations of AI Use
              </h3>
              <p className="text-gray-700 mb-3">Consistent with global AI governance standards, Ignite's AI does not:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-[#FF1200] mt-1 flex-shrink-0 font-bold">✗</span>
                  <span>Interpret community meaning, lived experience, or cultural context</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF1200] mt-1 flex-shrink-0 font-bold">✗</span>
                  <span>Judge, rank, score, or evaluate organizations or individuals</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF1200] mt-1 flex-shrink-0 font-bold">✗</span>
                  <span>Make funding, partnership, compliance, or eligibility decisions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF1200] mt-1 flex-shrink-0 font-bold">✗</span>
                  <span>Replace human facilitation, analysis, or collective sense-making</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                All interpretation, conclusions, and decisions remain human-led and community-informed.
              </p>
            </div>
          </section>

          {/* Human-in-the-Loop */}
          <section>
            <div className="border-l-4 border-[#FF9E80] pl-6 py-2">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Human-in-the-Loop and Community-in-the-Loop
              </h3>
              <p className="text-gray-700 mb-3">
                Ignite follows a human- and community-centered AI model, ensuring that:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Humans maintain authority over AI-supported outputs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>Communities closest to the data retain interpretive power</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">•</span>
                  <span>AI augments, not overrides, collective reasoning and accountability</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-3">
                This approach reflects best practices emphasizing human agency, oversight, and contestability in AI systems.
              </p>
            </div>
          </section>

          {/* Accountability */}
          <section>
            <div className="bg-gradient-to-br from-[#FFF5F0] to-[#FFF0E6] p-8 rounded-xl">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Accountability, Transparency, & Ethics
              </h3>
              <p className="text-gray-700 mb-6">Ignite's AI use is governed by the following principles:</p>
              
              <div className="space-y-5">
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-2">Human Agency & Oversight</h4>
                  <p className="text-gray-700">AI supports users but does not determine outcomes. Humans remain accountable for all decisions.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-2">Transparency & Explainability</h4>
                  <p className="text-gray-700">AI's role is clearly communicated to users, including what it does and does not do.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-2">Fairness & Harm Prevention</h4>
                  <p className="text-gray-700">AI is not used for predictive profiling, surveillance, or automated judgment that could produce discriminatory outcomes.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-2">Data Responsibility & Purpose Limitation</h4>
                  <p className="text-gray-700">AI processes only data intentionally provided by users for clearly defined purposes.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-2">Continuous Evaluation & Improvement</h4>
                  <p className="text-gray-700">AI use is reviewed and adjusted to ensure alignment with ethical standards and community values.</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-white/50 rounded-lg">
                <p className="font-semibold text-[#FF4D00] text-lg">
                  These commitments align directly with Measure's CARE Model: Community, Advocacy, Resilience, and Evidence.
                </p>
              </div>
            </div>
          </section>

          {/* Governing Frameworks */}
          <section>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              Governing Frameworks Referenced
            </h3>
            <p className="text-gray-700 mb-4">This policy is informed by the following widely recognized AI governance frameworks:</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-[#FF4D00] text-xl font-bold mt-1">→</span>
                <div>
                  <strong className="text-gray-900">OECD AI Principles (2019)</strong>
                  <p className="text-gray-700 text-sm mt-1">Human-centered values, transparency, accountability</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-[#FF4D00] text-xl font-bold mt-1">→</span>
                <div>
                  <strong className="text-gray-900">NIST AI Risk Management Framework (2023)</strong>
                  <p className="text-gray-700 text-sm mt-1">Govern, map, measure, and manage AI risks</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-[#FF4D00] text-xl font-bold mt-1">→</span>
                <div>
                  <strong className="text-gray-900">UNESCO Recommendation on the Ethics of AI (2021)</strong>
                  <p className="text-gray-700 text-sm mt-1">Human rights, cultural context, and social well-being</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-[#FF4D00] text-xl font-bold mt-1">→</span>
                <div>
                  <strong className="text-gray-900">EU Artificial Intelligence Act (adopted 2024)</strong>
                  <p className="text-gray-700 text-sm mt-1">Risk-based AI governance, human oversight, prohibited uses</p>
                </div>
              </div>
            </div>
          </section>

          {/* Closing Statement */}
          <section>
            <div className="bg-gradient-to-r from-[#FF4D00] to-[#E8973E] p-8 rounded-xl text-white">
              <h3 className="text-2xl font-semibold mb-4">
                Closing Statement
              </h3>
              <p className="text-lg leading-relaxed">
                Ignite was created by Measure to support equitable evaluation, community accountability, and responsible technology use. AI is a tool, not a judge, not an authority, and not a substitute for community wisdom.
              </p>
            </div>
          </section>
        </div>

        {/* Back to Home CTA */}
        <div className="mt-12 pt-8 border-t text-center">
          <Link to="/">
            <Button size="lg" className="bg-[#FF4D00] hover:bg-[#FF1200] gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row flex-wrap justify-between items-center gap-4">
            <div className="flex flex-wrap justify-center sm:justify-start gap-4 sm:gap-6 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#FF4D00]">
                Home
              </Link>
              <Link to="/ai-policy" className="hover:text-[#FF4D00]">
                AI Policy
              </Link>
              <a href="#" className="hover:text-[#FF4D00]">
                Privacy & Data Ethics
              </a>
              <a href="#" className="hover:text-[#FF4D00]">
                Contact
              </a>
            </div>
            <p className="text-sm text-gray-500">
              © Measure | Ignite Platform
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
